<template>
  <div
    class="try-eta-timer"
    :class="`try-eta-timer--${parsed.state}`"
    role="status"
    :aria-label="ariaLabel"
    aria-live="polite"
  >
    <span class="try-eta-timer-main">
      <span class="try-eta-timer-value">{{ parsed.value }}</span>
      <span v-if="parsed.unitKey" class="try-eta-timer-unit">{{ $t(parsed.unitKey) }}</span>
    </span>
  </div>
</template>

<script>
const ETA_MESSAGE_PATTERN = /^(Aprox\.|Menos de 1 min)/;

const HIDDEN_ETA_MESSAGES = new Set([
  "Listo",
  "No pudimos estimar el tiempo",
]);

export function isTryEtaHiddenMessage(message) {
  const normalized = String(message || "").trim();
  return HIDDEN_ETA_MESSAGES.has(normalized);
}

export function isTryEtaMessage(message) {
  const normalized = String(message || "").trim();
  if (!normalized || isTryEtaHiddenMessage(normalized)) return false;
  return ETA_MESSAGE_PATTERN.test(normalized);
}

function parseTryEtaMessage(message) {
  const normalized = String(message || "").trim();

  const approxMatch = normalized.match(/^Aprox\.\s+(\d+)\s+min restantes$/);
  if (approxMatch) {
    const minutes = approxMatch[1];
    return {
      state: "active",
      value: minutes,
      unitKey: "tryClips.loader.eta.minutesRemaining",
      ariaLabelKey: "tryClips.loader.eta.minutesRemainingAria",
      ariaLabelParams: { minutes },
    };
  }

  if (normalized === "Menos de 1 min restante") {
    return {
      state: "soon",
      value: "1",
      unitKey: "tryClips.loader.eta.minUnit",
      ariaLabelKey: "tryClips.loader.eta.oneMinuteAria",
      ariaLabelParams: {},
    };
  }

  return {
    state: "active",
    value: normalized,
    unitKey: "",
    ariaLabelKey: "",
    ariaLabelParams: {},
  };
}

export default {
  name: "TryEtaTimer",
  props: {
    message: {
      type: String,
      required: true,
    },
  },
  computed: {
    parsed() {
      return parseTryEtaMessage(this.message);
    },
    ariaLabel() {
      if (this.parsed.ariaLabelKey) {
        return this.$t(this.parsed.ariaLabelKey, this.parsed.ariaLabelParams);
      }
      return this.parsed.value;
    },
  },
};
</script>

<style scoped>
.try-eta-timer {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 40px;
  margin: 8px auto 0;
}

.try-eta-timer-main {
  display: inline-flex;
  align-items: baseline;
  justify-content: center;
  gap: 6px;
  width: 132px;
  min-height: 1.65rem;
  line-height: 1;
  flex-shrink: 0;
}

.try-eta-timer-value {
  font-size: 1.55rem;
  font-weight: 800;
  letter-spacing: -0.04em;
  font-variant-numeric: tabular-nums;
  background: linear-gradient(120deg, #f8fafc 0%, #ddd6fe 42%, #67e8f9 100%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

.try-eta-timer-unit {
  color: #cbd5e1;
  font-size: 0.82rem;
  font-weight: 600;
  letter-spacing: 0.01em;
  line-height: 1.2;
  white-space: nowrap;
}

.try-eta-timer--soon .try-eta-timer-value {
  background: linear-gradient(120deg, #fef3c7 0%, #fde68a 38%, #67e8f9 100%);
  -webkit-background-clip: text;
  background-clip: text;
}

@media (max-width: 560px) {
  .try-eta-timer-main {
    width: 124px;
  }

  .try-eta-timer-value {
    font-size: 1.42rem;
  }
}
</style>
